import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { useLanguage } from "@/hooks/useLanguage";
import { getDocumentTheme, subscribeSystemTheme, type ThemeMode } from "@/lib/theme";

/**
 * GalleryEntrance — premium "gallery threshold" overlay shown once per
 * browser session before the homepage hero is revealed.
 *
 * The entrance tracks real DOM image hydration with Promise.all and shows a
 * progress bar based on loaded image assets. No fake timers.
 */

const SESSION_KEY = "frankPistoriusEnteredShowroom";
const LEGACY_SESSION_KEY = "fp.entrance.entered.v1";

const HERO_POSTER = "/hero/poster.jpg";

const COPY = {
  en: { line1: "Lighting Sales Agency",        cta: "Enter the Showroom" },
  de: { line1: "Handelsagentur für Licht",     cta: "Showroom betreten" },
  it: { line1: "Agenzia di Illuminazione",     cta: "Entra nello showroom" },
  es: { line1: "Agencia de Iluminación",       cta: "Entrar al showroom" },
  nl: { line1: "Verlichtingsagentuur",         cta: "Betreed de showroom" },
} as const;

function isHomeRoot(pathname: string): boolean {
  const parts = pathname.replace(/^\/+/, "").replace(/\/+$/, "").split("/");
  if (parts.length === 0 || parts[0] === "") return true;
  if (parts.length === 1 && ["de","en","it","es","nl"].includes(parts[0])) return true;
  return false;
}

function alreadyEntered(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === "1"
        || sessionStorage.getItem(LEGACY_SESSION_KEY) === "1";
  } catch { return false; }
}

const GalleryEntrance = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const reduced = useReducedMotion();

  const [mounted, setMounted] = useState(() => {
    if (typeof window === "undefined") return false;
    if (alreadyEntered()) return false;
    return isHomeRoot(window.location.pathname);
  });
  const [stage, setStage] = useState<"idle" | "ready" | "exiting">("idle");
  const [progress, setProgress] = useState(0);
  const [assetsReady, setAssetsReady] = useState(false);
  const [theme, setTheme] = useState<ThemeMode>(() =>
    typeof document !== "undefined" ? getDocumentTheme() : "dark"
  );
  const overlayRef = useRef<HTMLDivElement>(null);

  // ── Bi-thematic dispatcher: react live to OS / theme flips while overlay is shown.
  useEffect(() => {
    setTheme(getDocumentTheme());
    const off = subscribeSystemTheme(() => setTheme(getDocumentTheme()));
    const mo = new MutationObserver(() => setTheme(getDocumentTheme()));
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["class", "data-theme"] });
    return () => { off(); mo.disconnect(); };
  }, []);

  const copy = COPY[language] ?? COPY.en;

  // Lock background scroll while overlay is visible.
  useEffect(() => {
    if (!mounted) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [mounted]);

  // Trigger reveal animation on next frame.
  useEffect(() => {
    if (!mounted) return;
    const r1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => setStage("ready"));
    });
    return () => {
      cancelAnimationFrame(r1);
    };
  }, [mounted]);

  // ── Real DOM image hydration tracker: Promise.all + HTMLImageElement.onload ──
  useEffect(() => {
    if (!mounted) return;
    let cancelled = false;
    const domImages = Array.from(document.images).filter((img) => !overlayRef.current?.contains(img));
    const poster = new Image();
    poster.src = HERO_POSTER;
    const assets: HTMLImageElement[] = [poster, ...domImages];
    const total = Math.max(assets.length, 1);
    let loaded = 0;
    const mark = () => {
      loaded += 1;
      if (!cancelled) setProgress(Math.round((loaded / total) * 100));
    };
    Promise.all(
      assets.map((img) => new Promise<void>((resolve) => {
        if (img.complete && img.naturalWidth > 0) {
          mark();
          resolve();
          return;
        }
        img.onload = () => { mark(); resolve(); };
        img.onerror = () => { mark(); resolve(); };
        if (!img.src && img.dataset?.src) img.src = img.dataset.src;
      })),
    ).then(() => {
      if (!cancelled) {
        setProgress(100);
        setAssetsReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [mounted]);

  const handleEnter = useCallback(() => {
    setStage((s) => (s === "exiting" ? s : "exiting"));
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
    requestAnimationFrame(() => {
      try {
        window.dispatchEvent(new CustomEvent("fp:hero-reveal"));
        window.dispatchEvent(new CustomEvent("fp:hero-play"));
      } catch { /* ignore */ }
    });
  }, []);

  // Gated click — ignore until DOM image hydration is complete.
  const handleEnterGated = useCallback(() => {
    if (!assetsReady) return;
    handleEnter();
  }, [assetsReady, handleEnter]);

  // Unmount overlay if route changes while still showing.
  useEffect(() => {
    if (!mounted) return;
    if (!isHomeRoot(location.pathname)) {
      try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
      setMounted(false);
    }
  }, [location.pathname, mounted]);

  // Global key handler.
  useEffect(() => {
    if (!mounted) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " " || e.key === "Escape") {
        e.preventDefault();
        handleEnterGated();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mounted, handleEnterGated]);

  if (!mounted) return null;

  const visible = stage === "ready";
  const exiting = stage === "exiting";
  const ease = "cubic-bezier(0.16, 1, 0.3, 1)";
  const easeSoft = "cubic-bezier(0.22, 1, 0.36, 1)";

  const isDark = theme === "dark";
  // Pure-canvas backdrop: solid black for dark, solid white for light.
  const floorBg = isDark ? "#000000" : "#FFFFFF";
  const textPrimary = isDark ? "#F5F2EB" : "#111111";
  const textMuted = isDark ? "rgba(245,242,235,0.66)" : "rgba(17,17,17,0.62)";
  const textShadow = isDark
    ? "0 1px 30px rgba(0,0,0,0.5)"
    : "0 1px 30px rgba(0,0,0,0.06)";
  const vignette = isDark
    ? "inset 0 0 240px 70px rgba(0,0,0,0.6)"
    : "inset 0 0 240px 70px rgba(0,0,0,0.04)";
  const plumeOpacity = visible ? (isDark ? 0.55 : 0.3) : 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-hidden={exiting ? "true" : "false"}
      aria-label="Frank Pistorius — gallery entrance"
      data-theme={theme}
      data-ready={assetsReady ? "true" : "false"}
      ref={overlayRef}
      onClick={handleEnterGated}
      onTransitionEnd={(e) => {
        if (e.propertyName === "opacity" && exiting) setMounted(false);
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483646,
        // Absolute event lock: shield all input while assets are caching.
        pointerEvents: exiting ? "none" : "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        paddingTop: "max(env(safe-area-inset-top, 0px), 24px)",
        paddingBottom: "max(env(safe-area-inset-bottom, 0px), 32px)",
        paddingLeft: "24px",
        paddingRight: "24px",
        cursor: assetsReady ? "pointer" : "wait",
        color: textPrimary,
        minHeight: "100svh",
        opacity: exiting ? 0 : 1,
        transition: `opacity 1500ms ${easeSoft}, background 600ms ${easeSoft}, color 600ms ${easeSoft}`,
        background: floorBg,
      }}
    >
      {/* ── Neutral pearl / silver / white plume — Gemini generative glow ── */}
      <div
        aria-hidden="true"
        className="fp-generative-glow is-active entrance-plume"
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: plumeOpacity,
          mixBlendMode: isDark ? "normal" : "soft-light",
          transition: `opacity 1400ms ${easeSoft}`,
        }}
      />

      {/* Inner vignette */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          boxShadow: vignette,
        }}
      />

      {/* Soft light bloom — neutral, theme-aware */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: isDark
            ? "radial-gradient(ellipse 60% 45% at 50% 52%, rgba(245,242,235,0.08) 0%, transparent 70%)"
            : "radial-gradient(ellipse 60% 45% at 50% 52%, rgba(255,255,255,0.55) 0%, transparent 70%)",
          opacity: visible ? 1 : 0,
          transition: reduced ? `opacity 360ms ${easeSoft}` : `opacity 2000ms ${easeSoft}`,
        }}
      />


      {/* ── Wordmark + subtitle + CTA ── */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          maxWidth: "min(820px, 94vw)",
          gap: "clamp(16px, 2.6vh, 30px)",
        }}
      >
        <h1
          className="font-display"
          style={{
            margin: 0,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.22em",
            fontSize: "clamp(2rem, 6.6vw, 5.2rem)",
            lineHeight: 1.05,
            color: textPrimary,
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(18px)",
            filter: visible ? "blur(0px)" : "blur(10px)",
            transition: reduced
              ? `opacity 360ms ${easeSoft}`
              : `opacity 1700ms ${ease} 180ms, transform 1700ms ${ease} 180ms, filter 1700ms ${ease} 180ms`,
            textShadow,
          }}
        >
          Frank Pistorius
        </h1>

        <p
          style={{
            margin: 0,
            fontSize: "clamp(0.78rem, 1.6vw, 0.95rem)",
            letterSpacing: "0.36em",
            textTransform: "uppercase",
            color: textMuted,
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(12px)",
            filter: visible ? "blur(0px)" : "blur(7px)",
            transition: reduced
              ? `opacity 320ms ${easeSoft} 80ms`
              : `opacity 1200ms ${ease} 700ms, transform 1200ms ${ease} 700ms, filter 1200ms ${ease} 700ms`,
          }}
        >
          {copy.line1}
        </p>


        <button
          type="button"
          autoFocus
          disabled={!assetsReady}
          onClick={(e) => { e.stopPropagation(); handleEnterGated(); }}
          aria-label={copy.cta}
          aria-disabled={!assetsReady}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(assetsReady ? 100 : progress)}
          data-ready={assetsReady ? "true" : "false"}
          className="entrance-cta"
          style={{
            marginTop: "clamp(28px, 5.5vh, 56px)",
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(12px)",
            pointerEvents: assetsReady ? "auto" : "none",
            cursor: assetsReady ? "pointer" : "wait",
            transition: reduced
              ? `opacity 320ms ${easeSoft} 160ms`
              : `opacity 700ms ${easeSoft}, transform 1100ms ${ease} 1300ms`,
          }}
        >
          {/* Progress fill — expands left→right behind the label while loading */}
          <span
            aria-hidden="true"
            className="entrance-cta__fill"
            style={{
              transform: `scaleX(${assetsReady ? 1 : Math.max(0, Math.min(1, progress / 100))})`,
            }}
          />
          <span className="entrance-cta__label">{copy.cta}</span>
        </button>
      </div>

      <style>{`
        .entrance-cta {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 52px;
          padding: 14px 38px;
          border-radius: 9999px;
          font-size: clamp(0.82rem, 1.4vw, 0.95rem);
          letter-spacing: 0.28em;
          text-transform: uppercase;
          backdrop-filter: blur(24px) saturate(140%);
          -webkit-backdrop-filter: blur(24px) saturate(140%);
          transition:
            background-color 700ms ${easeSoft},
            border-color 700ms ${easeSoft},
            color 700ms ${easeSoft},
            letter-spacing 320ms ${easeSoft},
            box-shadow 500ms ${easeSoft},
            transform 260ms cubic-bezier(0.2, 0, 0, 1);
          outline: none;
        }
        /* DARK MODE — white fill sweeps over dark capsule */
        [data-theme="dark"] .entrance-cta {
          background: rgba(10, 10, 10, 0.55);
          border: 1px solid rgba(245, 242, 235, 0.14);
          color: #F5F2EB;
        }
        [data-theme="dark"] .entrance-cta__fill { background: #FFFFFF; }
        [data-theme="dark"] .entrance-cta__label { color: #F5F2EB; }
        [data-theme="dark"] .entrance-cta[data-ready="true"] {
          background: #FFFFFF;
          border-color: rgba(255, 255, 255, 0.9);
          color: #0A0A0A;
        }
        [data-theme="dark"] .entrance-cta[data-ready="true"] .entrance-cta__label {
          color: #0A0A0A;
        }
        /* LIGHT MODE — charcoal fill sweeps over pearl capsule */
        [data-theme="light"] .entrance-cta {
          background: rgba(255, 255, 255, 0.65);
          border: 1px solid rgba(17, 17, 17, 0.10);
          color: #111111;
        }
        [data-theme="light"] .entrance-cta__fill { background: #111111; }
        [data-theme="light"] .entrance-cta__label { color: #111111; }
        [data-theme="light"] .entrance-cta[data-ready="true"] {
          background: #111111;
          border-color: rgba(17, 17, 17, 0.9);
          color: #FAFAFA;
        }
        [data-theme="light"] .entrance-cta[data-ready="true"] .entrance-cta__label {
          color: #FAFAFA;
        }
        .entrance-cta__fill {
          position: absolute;
          inset: 0;
          z-index: 0;
          transform-origin: left center;
          transition: transform 320ms ${easeSoft}, opacity 600ms ${easeSoft};
          pointer-events: none;
          border-radius: 9999px;
          will-change: transform;
        }
        .entrance-cta__label {
          position: relative;
          z-index: 1;
          /* difference auto-inverts letters as the fill sweeps under them */
          mix-blend-mode: difference;
          transition: color 700ms ${easeSoft};
        }
        .entrance-cta[data-ready="true"] .entrance-cta__fill {
          transform: scaleX(1) !important;
          opacity: 0;
        }
        .entrance-cta[data-ready="true"] .entrance-cta__label {
          mix-blend-mode: normal;
        }
        .entrance-cta[data-ready="true"]:hover {
          transform: scale(1.02);
          letter-spacing: 0.32em;
          box-shadow: 0 12px 40px -10px rgba(0, 0, 0, 0.25);
        }
        .entrance-cta[data-ready="true"]:active {
          transform: scale(0.985);
        }
        .entrance-cta:focus-visible {
          box-shadow: 0 0 0 2px rgba(150, 150, 150, 0.45);
        }
      `}</style>
    </div>
  );
};

export default GalleryEntrance;
